import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import { NameHelpers, Patient } from '@principle-theorem/principle-core';
import {
  AccAddressType,
  AccEthnicityCode,
  AccGender,
  ContactNumberLabel,
  IAccClaimPatientDetails,
  IAccContact,
  IAccPatient,
  IContactNumber,
  IPatient,
  OTHER_ETHNICITY_CODE,
  PRINCIPLE_TO_ACC_GENDER_MAP,
} from '@principle-theorem/principle-core/interfaces';
import { isISODateType, ISODateType, WithRef } from '@principle-theorem/shared';
import { AccAddressFormGroup } from './acc-address.formgroup';
import { hasRequiredError, setRequiredValidation } from './form-group-helpers';

export type IAccPatientForm = Omit<IAccPatient, 'occupationCode'>;

export class AccPatientFormGroup extends TypedFormGroup<IAccPatientForm> {
  constructor() {
    super({
      details: new AccPatientDetailsFormGroup(),
      // occupationCode - handled separately in employment form
      contact: new AccPatientContactFormGroup(),
      address: new AccAddressFormGroup(AccAddressType.Home),
    });
  }

  async patchPatientData(patient: WithRef<IPatient>): Promise<void> {
    const contactNumbers = await Patient.resolveContactNumbers(patient);
    const mobilePhone = await Patient.resolveMobileNumber(patient);

    this._patchDetailsForm(patient);
    this._patchAddressForm(patient);
    this._patchContactForm(patient, contactNumbers, mobilePhone);
    this.markAllAsTouched();
  }

  get details(): AccPatientDetailsFormGroup {
    return this.controls.details as AccPatientDetailsFormGroup;
  }

  get contact(): AccPatientContactFormGroup {
    return this.controls.contact as AccPatientContactFormGroup;
  }

  get address(): AccAddressFormGroup {
    return this.controls.address as AccAddressFormGroup;
  }

  private _patchDetailsForm(patient: WithRef<IPatient>): void {
    this.details.patchValue({
      nhi: patient.nationalHealthIndexNumber,
      firstName: NameHelpers.firstName(patient.name),
      surname: NameHelpers.lastName(patient.name),
      dateOfBirth: patient.dateOfBirth,
      gender: PRINCIPLE_TO_ACC_GENDER_MAP[patient.gender],
    });
  }

  private _patchAddressForm(patient: WithRef<IPatient>): void {
    this.address.convertAddressComponents(
      patient.metadata?.address?.components
    );
  }

  private _patchContactForm(
    patient: WithRef<IPatient>,
    contactNumbers: IContactNumber[],
    mobilePhone?: string
  ): void {
    this.contact.patchValue({
      emailAddress: patient.email,
      mobilePhone,
      homePhone: this._contactNumber(contactNumbers, ContactNumberLabel.Home),
      workPhone: this._contactNumber(contactNumbers, ContactNumberLabel.Work),
    });
  }

  private _contactNumber(
    contactNumbers: IContactNumber[],
    label: ContactNumberLabel
  ): string | undefined {
    const contact = contactNumbers.find((number) => number.label === label);
    return contact?.number;
  }
}

class AccPatientDetailsFormGroup extends TypedFormGroup<IAccClaimPatientDetails> {
  constructor() {
    super({
      firstName: new TypedFormControl<string>('', Validators.required),
      middleName: new TypedFormControl<string>(''),
      surname: new TypedFormControl<string>('', Validators.required),
      nhi: new TypedFormControl<string>(''),
      dateOfBirth: new TypedFormControl<ISODateType>(
        undefined,
        Validators.required
      ).withGuard(isISODateType),
      gender: new TypedFormControl<AccGender>(undefined, Validators.required),
      ethnicityCode: new TypedFormControl<AccEthnicityCode>(
        undefined,
        Validators.required
      ),
      otherEthnicity: new TypedFormControl<string>(''),
    });

    this.controls.ethnicityCode.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) =>
        setRequiredValidation(
          value === OTHER_ETHNICITY_CODE,
          this.controls.otherEthnicity
        )
      );
  }

  get firstNameRequiredError(): boolean {
    return hasRequiredError(this.controls.firstName);
  }

  get surnameRequiredError(): boolean {
    return hasRequiredError(this.controls.surname);
  }

  get dateOfBirthRequiredError(): boolean {
    return hasRequiredError(this.controls.dateOfBirth);
  }

  get genderRequiredError(): boolean {
    return hasRequiredError(this.controls.gender);
  }

  get displayOtherEthnicity(): boolean {
    return (
      this.controls.ethnicityCode.touched &&
      this.controls.ethnicityCode.value === OTHER_ETHNICITY_CODE
    );
  }

  get ethnicityCodeRequiredError(): boolean {
    return hasRequiredError(this.controls.ethnicityCode);
  }

  get otherEthnicityRequiredError(): boolean {
    return hasRequiredError(this.controls.otherEthnicity);
  }
}

class AccPatientContactFormGroup extends TypedFormGroup<IAccContact> {
  constructor() {
    super({
      emailAddress: new TypedFormControl<string>('', Validators.email),
      mobilePhone: new TypedFormControl<string>(''),
      homePhone: new TypedFormControl<string>(''),
      workPhone: new TypedFormControl<string>(''),
    });
  }

  get emailAddressValidationError(): boolean {
    return hasRequiredError(this.controls.emailAddress);
  }
}
